import React from 'react';
import Modal from 'react-modal';
import { Field, Form, Formik } from 'formik';
import { Oval } from 'react-loader-spinner';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

import { useAppSelector, useAppDispatch } from '../../../app/hooks';
import { toggleShowAddDumpYardModal, createDumpYardTemplate } from '../dumpYardsSlice';
import { ReactComponent as Close } from '../../../assets/images/close.svg';
import { RadioButton } from '../../Shared';
import { DumpYardTemplate, DumpYardTemplateType, WASTE_TYPES, wasteTypeLabels, hhmmToMinutes, DEFAULT_OPEN_HHMM, DEFAULT_CLOSE_HHMM } from '../../../models';
import { DumpYardTemplateForm } from '../../../datasource';

import './AddDumpYardModal.scss';

// Operating hours apply to dump yards AND storages (not quick stops).
const hasOperatingHours = (type: DumpYardTemplateType) =>
  type === DumpYardTemplateType.DUMP_YARD || type === DumpYardTemplateType.STORAGE;

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: "#FFFFFF"
  },
};

const validationSchema = Yup.object().shape({
  address: Yup.string().required('Address is required'),
  zip: Yup.string().required('ZIP is required'),
  pricePerTon: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .when('type', {
      is: DumpYardTemplateType.DUMP_YARD,
      then: (schema) =>
        schema
          .typeError('Enter a valid price')
          .positive('Dumping price must be greater than 0')
          .required('Dumping price is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  minDumpWeight: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .when('type', {
      is: DumpYardTemplateType.DUMP_YARD,
      then: (schema) =>
        schema
          .typeError('Enter a valid weight')
          .positive('Minimum dump weight must be greater than 0')
          .notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),
  serviceTimeOverride: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .when('type', {
      is: DumpYardTemplateType.DUMP_YARD,
      then: (schema) =>
        schema
          .typeError('Enter whole minutes')
          .integer('Enter whole minutes')
          .positive('Service time override must be greater than 0')
          .notRequired(),
      otherwise: (schema) => schema.notRequired(),
    }),
  wasteTypes: Yup.array()
    .of(Yup.string())
    .when('type', {
      is: DumpYardTemplateType.DUMP_YARD,
      then: (schema) => schema.min(1, 'Select at least one waste type'),
      otherwise: (schema) => schema,
    }),
  operatingHoursEnd: Yup.string().when('type', {
    is: (t: DumpYardTemplateType) => hasOperatingHours(t),
    then: (schema) =>
      schema.test('after-open', 'Close time must be after open time', function (value) {
        const start = this.parent.operatingHoursStart as string;
        const s = hhmmToMinutes(start);
        const e = hhmmToMinutes(value ?? '');
        if (s == null || e == null) return true; // blank = backend default; nothing to compare
        return e > s;
      }),
    otherwise: (schema) => schema,
  }),
});

export type AddDumpYardFormModel = Pick<DumpYardTemplate, 'address' | 'zip' | 'name' | 'isDumpYard' | 'isStorage' | 'isQuickStop' | 'isActive'> & { type: DumpYardTemplateType; pricePerTon: number | string; minDumpWeight: number | string; serviceTimeOverride: number | string; operatingHoursStart: string; operatingHoursEnd: string; wasteTypes: string[] }

const initialValues: AddDumpYardFormModel = {
  address: '',
  zip: '',
  name: '',
  isDumpYard: false,
  isStorage: false,
  isQuickStop: false,
  isActive: true,
  type: DumpYardTemplateType.DUMP_YARD,
  pricePerTon: '',
  minDumpWeight: '',
  serviceTimeOverride: '',
  operatingHoursStart: DEFAULT_OPEN_HHMM,
  operatingHoursEnd: DEFAULT_CLOSE_HHMM,
  wasteTypes: [],
};

export function AddDumpYardModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(state => state.dumpYards.showAddDumpYardModal);
  const addDumpYardAPI = useAppSelector(state => state.dumpYards.createDumpYardTemplate);

  React.useEffect(() => {
    if (!addDumpYardAPI.loading && !!addDumpYardAPI.error) {
      toast.error('Something went wrong. Please try again.');
    }

    if (!addDumpYardAPI.loading && !addDumpYardAPI.error && addDumpYardAPI.value) {
      toast.success('Successfully added dump yard.');
      dispatch(toggleShowAddDumpYardModal());
    }
  }, [addDumpYardAPI.loading, !!addDumpYardAPI.error, !!addDumpYardAPI.value]);

  const toggleModal = () => {
    dispatch(toggleShowAddDumpYardModal());
  }

  const onSubmit = (form: AddDumpYardFormModel) => {
    const isDumpYard = form.type === DumpYardTemplateType.DUMP_YARD;
    const facility = hasOperatingHours(form.type);
    const payload: DumpYardTemplateForm = {
      address: form.address,
      zip: form.zip,
      name: form.name,
      isDumpYard,
      isStorage: form.type === DumpYardTemplateType.STORAGE,
      isQuickStop: form.type === DumpYardTemplateType.QUICK_STOP,
      isActive: form.isActive,
      pricePerTon: isDumpYard ? Number(form.pricePerTon) : null,
      minDumpWeight: isDumpYard && form.minDumpWeight !== '' ? Number(form.minDumpWeight) : null,
      serviceTimeOverride: isDumpYard && form.serviceTimeOverride !== '' ? Number(form.serviceTimeOverride) : null,
      operatingHoursStart: facility ? hhmmToMinutes(form.operatingHoursStart) : null,
      operatingHoursEnd: facility ? hhmmToMinutes(form.operatingHoursEnd) : null,
      wasteTypes: isDumpYard ? form.wasteTypes : [],
    };
    dispatch(createDumpYardTemplate(payload));
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={toggleModal}
      style={modalStyles}
    >
      <div className='add-dump-yard-modal-component'>
        <Close className='close-button' onClick={toggleModal}/>

        <div className='title'>Add Dump Yard</div>
        
        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched, values }) => (
            <Form className='add-dump-yard-form'>
              <div className='field-container'>
                <div className='label'>Address:</div>
                <Field id='address' name='address' className='text-input'/>
                {errors.address && touched.address && <div className='form-error'>{errors.address}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>Name:</div>
                <Field id='name' name='name' className='text-input'/>
                {errors.name && touched.name && <div className='form-error'>{errors.name}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>ZIP:</div>
                <Field id='zip' name='zip' className='text-input'/>
                {errors.zip && touched.zip && <div className='form-error'>{errors.zip}</div>}
              </div>

              <div className='field-container'>
                <div className='label'>Type:</div>
                <div className='radio-button-options-container'>
                  <RadioButton name="type" value={DumpYardTemplateType.DUMP_YARD} label='Dump Yard'/>
                  <RadioButton name="type" value={DumpYardTemplateType.STORAGE} label='Storage'/>
                  <RadioButton name="type" value={DumpYardTemplateType.QUICK_STOP} label='Quick Stop'/>
                </div>
              </div>

              {values.type === DumpYardTemplateType.DUMP_YARD && (
                <div className='field-container'>
                  <label className='active-toggle'>
                    <Field type='checkbox' name='isActive' />
                    <span>Active (uncheck to stop routing here without deleting)</span>
                  </label>
                </div>
              )}

              {hasOperatingHours(values.type) && (
                <div className='field-container'>
                  <div className='label'>Operating hours:</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Field id='operatingHoursStart' name='operatingHoursStart' type='time' className='text-input' aria-label='Opens' />
                    <span>–</span>
                    <Field id='operatingHoursEnd' name='operatingHoursEnd' type='time' className='text-input' aria-label='Closes' />
                  </div>
                  {errors.operatingHoursEnd && touched.operatingHoursEnd && <div className='form-error'>{errors.operatingHoursEnd as string}</div>}
                </div>
              )}

              {values.type === DumpYardTemplateType.DUMP_YARD && (
                <div className='field-container'>
                  <div className='label'>Dumping Price ($/ton):</div>
                  <Field id='pricePerTon' name='pricePerTon' type='number' step='0.01' min='0.01' className='text-input'/>
                  {errors.pricePerTon && touched.pricePerTon && <div className='form-error'>{errors.pricePerTon}</div>}
                </div>
              )}

              {values.type === DumpYardTemplateType.DUMP_YARD && (
                <div className='field-container'>
                  <div className='label'>Minimum Dump Weight (tons):</div>
                  <Field id='minDumpWeight' name='minDumpWeight' type='number' step='0.01' min='0.01' className='text-input'/>
                  {errors.minDumpWeight && touched.minDumpWeight && <div className='form-error'>{errors.minDumpWeight}</div>}
                </div>
              )}

              {values.type === DumpYardTemplateType.DUMP_YARD && (
                <div className='field-container'>
                  <div className='label'>Service Time Override (minutes):</div>
                  <Field id='serviceTimeOverride' name='serviceTimeOverride' type='number' step='1' min='1' placeholder='Default 15' className='text-input'/>
                  {errors.serviceTimeOverride && touched.serviceTimeOverride && <div className='form-error'>{errors.serviceTimeOverride}</div>}
                </div>
              )}

              {values.type === DumpYardTemplateType.DUMP_YARD && (
                <div className='field-container'>
                  <div className='label'>Accepted waste types:</div>
                  <div className='waste-types-options'>
                    {WASTE_TYPES.map((w) => (
                      <label key={w} className='waste-type-option'>
                        <Field type='checkbox' name='wasteTypes' value={w} />
                        <span>{wasteTypeLabels[w]}</span>
                      </label>
                    ))}
                  </div>
                  {errors.wasteTypes && touched.wasteTypes && <div className='form-error'>{errors.wasteTypes as string}</div>}
                </div>
              )}

              <button type='submit' className='add-button'  disabled={addDumpYardAPI.loading}>
                Add Dump Yard
                {addDumpYardAPI.loading && <div style={{ marginLeft: 8}}><Oval color="#131313" height={20} width={20} secondaryColor="#FBD20A"/></div>}
              </button>

              {!!addDumpYardAPI.error && (
                <div className='error-text'>
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
